import type { ReactNode } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { IconButton } from '../index';

import type { useTheme } from './theme';
import type { makeStyles } from './ExerciseSwapPicker.styles';

// EQ-1-GYM-COLDSTART R4 — what a declared-missing exercise looks like at the
// bottom of the list, and how it comes back.
//
// ⛔ It SINKS, it is never filtered out (Antenore, equipment-model.md §11.7 —
// the same rule slice D4 already follows for the place ranking). An exercise
// the app has decided does not exist here is the dead end EQ-1 keeps having to
// un-build: you stand in front of the machine and there is no way forward.
// Sunk plus marked says the same thing and costs one scroll.
//
// ⭐ It WRAPS the candidate row instead of editing it. `_ExerciseSwapPickerRow`
// documents having been deliberately lightened, and its header names the
// per-row-control failure mode; the badge and the ✚ therefore live beside it,
// in a composition, and that file stays untouched.
//
// ⚠️ No `accessibilityLabel` on the wrapper, deliberately. A label on a
// container collapses the announcement of its whole subtree — the trap already
// fixed twice in this component family — and would take the row's own composed
// label AND the ✚'s with it. Every node here speaks for itself.
type Props = {
  /** The unmodified <ExerciseSwapPickerRow>. */
  children: ReactNode;
  /** Undoes the WHOLE declaration for this exercise here (restoreAvailable). */
  onRestore: () => void;
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
};

export function UnavailableCandidateRow({
  children,
  onRestore,
  theme,
  styles,
}: Props) {
  const { t } = useTranslation();
  return (
    <View style={styles.unavailableRow}>
      <View style={styles.unavailableRowMain}>
        {children}
        <View style={styles.unavailableBadge}>
          <Text style={styles.unavailableBadgeText}>
            {t('swap.unavailableBadge', { defaultValue: 'Not here' })}
          </Text>
        </View>
      </View>
      {/* The inverse of the ✕, where the effect of the ✕ is visible: an error
          is repaired where it shows. ⚠️ The left slop is 6 into an 8pt margin,
          not the full 8: the row beside it is one big Pressable with no slop
          of its own, so overlapping zones would make the restore fire a swap
          instead. 2pt stay clear, and the box reads 50×52 instead of 36×36. */}
      <IconButton
        theme={theme}
        name="plus"
        style={styles.unavailableRestore}
        hitSlop={{ top: 8, bottom: 8, left: 6, right: 8 }}
        accessibilityLabel={t('swap.unavailableRestore', {
          defaultValue: 'Available here again',
        })}
        onPress={onRestore}
      />
    </View>
  );
}
